import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global attribution capture. Listens on every route change and copies any
 * `?ref=`, `?aff=`, `?p=`, `?partner=`, `?src=` from the URL into a
 * **90-day cookie** + matching localStorage entry — so every lead-capture
 * form on every page can credit the partner, even months after first click.
 *
 * Cookies set (consistent with the sister sites' attribution policy):
 *   wts_ref                 → partner / affiliate code (90 days)
 *   wts_signup_source       → src= attribution tag (90 days)
 *
 * 90-day window is the same one charityfundraiser.com / localbrandingpro.com
 * / jobdispatchpro.com use, so attribution stays consistent across the
 * Web-TechServices portfolio. Don't change without coordinating with all
 * sister-site engineers.
 */

const ATTRIBUTION_DAYS = 90;
const COOKIE_REF = 'wts_ref';
const COOKIE_SRC = 'wts_signup_source';


function setCookie(name, value, days) {
  if (typeof document === 'undefined' || !name || !value) return;
  try {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    // SameSite=Lax so it survives standard cross-site redirects (e.g. the
    // sister-site bouncer 302). Secure flag set when on HTTPS.
    const secure = (typeof location !== 'undefined' && location.protocol === 'https:') ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
  } catch (e) {
    // never throw from attribution code
  }
}


function readCookie(name) {
  if (typeof document === 'undefined') return '';
  try {
    const target = `${name}=`;
    const parts = document.cookie.split(';');
    for (const p of parts) {
      const trimmed = p.trim();
      if (trimmed.startsWith(target)) {
        return decodeURIComponent(trimmed.slice(target.length));
      }
    }
  } catch (e) {
    // ignore
  }
  return '';
}


export default function RefAttributionCapture() {
  const { search } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(search);
      const ref = (params.get('ref') || params.get('aff') || params.get('p') || params.get('partner') || '').trim();
      const src = (params.get('src') || '').trim();
      if (ref) {
        setCookie(COOKIE_REF, ref, ATTRIBUTION_DAYS);
        // Mirror to localStorage for in-app readers that don't parse cookies.
        try { localStorage.setItem(COOKIE_REF, ref); } catch (e) { /* ignore */ }
      }
      if (src) {
        setCookie(COOKIE_SRC, src, ATTRIBUTION_DAYS);
        try { localStorage.setItem(COOKIE_SRC, src); } catch (e) { /* ignore */ }
      }
    } catch (e) {
      // fail silently
    }
  }, [search]);

  return null;
}

/** Helper for forms: returns the captured ref code, or '' if none.
 *  Checks cookie first (90-day), then localStorage (mirror), then
 *  sessionStorage (legacy session-only fallback). */
export function getCapturedRef() {
  const fromCookie = readCookie(COOKIE_REF);
  if (fromCookie) return fromCookie.trim();
  try {
    const ls = (localStorage.getItem(COOKIE_REF) || '').trim();
    if (ls) return ls;
  } catch (e) { /* ignore */ }
  try {
    return (sessionStorage.getItem(COOKIE_REF) || '').trim();
  } catch (e) {
    return '';
  }
}

/** Helper for forms: returns the captured src tag, or '' if none. */
export function getCapturedSource() {
  const fromCookie = readCookie(COOKIE_SRC);
  if (fromCookie) return fromCookie.trim();
  try {
    const ls = (localStorage.getItem(COOKIE_SRC) || '').trim();
    if (ls) return ls;
  } catch (e) { /* ignore */ }
  try {
    return (sessionStorage.getItem(COOKIE_SRC) || '').trim();
  } catch (e) {
    return '';
  }
}
